import { useRef, useCallback, useState } from "react";

interface TimelineProps {
  currentTime: number;
  duration: number;
  markIn: number | null;
  markOut: number | null;
  onSeek: (time: number) => void;
  onMarkInChange: (time: number | null) => void;
  onMarkOutChange: (time: number | null) => void;
}

type DragTarget = "playhead" | "markIn" | "markOut" | null;

export default function Timeline({
  currentTime,
  duration,
  markIn,
  markOut,
  onSeek,
  onMarkInChange,
  onMarkOutChange,
}: TimelineProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<DragTarget>(null);
  const [hoverTime, setHoverTime] = useState<number | null>(null);

  const timeToPercent = (t: number) => (duration > 0 ? (t / duration) * 100 : 0);

  const xToTime = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track || duration <= 0) return 0;
      const rect = track.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return pct * duration;
    },
    [duration],
  );

  const handlePointerDown = useCallback((e: React.PointerEvent, target: DragTarget) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(target);
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const t = xToTime(e.clientX);
      if (!dragging) { setHoverTime(t); return; }
      if (dragging === "playhead") {
        onSeek(t);
      } else if (dragging === "markIn") {
        const clamped = markOut !== null ? Math.min(t, markOut - 0.05) : t;
        onMarkInChange(Math.max(0, clamped));
      } else if (dragging === "markOut") {
        const clamped = markIn !== null ? Math.max(t, markIn + 0.05) : t;
        onMarkOutChange(Math.min(duration, clamped));
      }
    },
    [dragging, xToTime, onSeek, onMarkInChange, onMarkOutChange, markIn, markOut, duration],
  );

  const handlePointerUp = useCallback(() => { setDragging(null); }, []);

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).dataset.handle) return;
      onSeek(xToTime(e.clientX));
    },
    [xToTime, onSeek],
  );

  const playheadPct = timeToPercent(currentTime);
  const markInPct = markIn !== null ? timeToPercent(markIn) : null;
  const markOutPct = markOut !== null ? timeToPercent(markOut) : null;

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div
      ref={trackRef}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={() => { setHoverTime(null); if (dragging) setDragging(null); }}
      onClick={handleTrackClick}
      className="flex-1 relative h-7 select-none touch-none"
      style={{ cursor: dragging ? "grabbing" : "pointer" }}
    >
      {/* Track background */}
      <div className="absolute top-2.5 left-0 right-0 h-2 bg-neutral-700 rounded" />

      {/* Progress fill */}
      <div
        className="absolute top-2.5 left-0 h-2 bg-neutral-500 rounded"
        style={{ width: `${playheadPct}%` }}
      />

      {/* Mark region highlight */}
      {(markInPct !== null || markOutPct !== null) && (
        <div
          className="absolute top-2 h-3 bg-green-500/35 rounded-sm"
          style={{
            left: `${markInPct ?? 0}%`,
            width: `${(markOutPct ?? 100) - (markInPct ?? 0)}%`,
          }}
        />
      )}

      {/* Mark-in handle */}
      {markInPct !== null && (
        <div
          data-handle="markIn"
          onPointerDown={(e) => handlePointerDown(e, "markIn")}
          className="absolute -top-1 -translate-x-1/2 w-8 h-9 flex items-center justify-center z-[3]"
          style={{ left: `${markInPct}%`, cursor: dragging === "markIn" ? "grabbing" : "grab" }}
        >
          <div
            data-handle="markIn"
            className="w-1 h-5 bg-green-500 rounded-sm"
            style={{ boxShadow: dragging === "markIn" ? "0 0 6px rgba(80, 180, 80, 0.8)" : "none" }}
          />
        </div>
      )}

      {/* Mark-out handle */}
      {markOutPct !== null && (
        <div
          data-handle="markOut"
          onPointerDown={(e) => handlePointerDown(e, "markOut")}
          className="absolute -top-1 -translate-x-1/2 w-8 h-9 flex items-center justify-center z-[3]"
          style={{ left: `${markOutPct}%`, cursor: dragging === "markOut" ? "grabbing" : "grab" }}
        >
          <div
            data-handle="markOut"
            className="w-1 h-5 bg-green-500 rounded-sm"
            style={{ boxShadow: dragging === "markOut" ? "0 0 6px rgba(80, 180, 80, 0.8)" : "none" }}
          />
        </div>
      )}

      {/* Playhead */}
      <div
        data-handle="playhead"
        onPointerDown={(e) => handlePointerDown(e, "playhead")}
        className="absolute -top-0.5 -translate-x-1/2 w-8 h-8 flex items-center justify-center z-[4]"
        style={{ left: `${playheadPct}%`, cursor: dragging === "playhead" ? "grabbing" : "grab" }}
      >
        <div
          data-handle="playhead"
          className="w-3 h-3 bg-white rounded-full border-2 border-gray-500"
          style={{ boxShadow: dragging === "playhead" ? "0 0 6px rgba(255,255,255,0.6)" : "none" }}
        />
      </div>

      {/* Hover time tooltip */}
      {hoverTime !== null && !dragging && (
        <div
          className="absolute -top-5 -translate-x-1/2 bg-neutral-800 text-neutral-400 px-1.5 py-px rounded text-[10px] pointer-events-none whitespace-nowrap"
          style={{ left: `${timeToPercent(hoverTime)}%` }}
        >
          {formatTime(hoverTime)}
        </div>
      )}
    </div>
  );
}
